import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModalDialog from '../../ModalDialog';
import UploadFilesButton from '../../buttons/UploadFilesButton';
import ChooseFileButton from '../../buttons/ChooseFileButton';
import { getFileUploads, getPrivateConfig } from '../../../reducers';
import {
    initFileUploads,
    removeAllFileUploads,
    removeFileUpload,
} from '../../../actions/fileUploads';
import useEffectOnLoad from '../../../hooks/useEffectOnLoad';
import UploadFileRow from './UploadFileRow';
import { FileUploadState } from '../../../reducers/fileUploads';
import ConfirmPopup from '../ConfirmPopup';
import './_styles_/UploadPopup.css';
import { fileUploadVariablesType } from '../../../types';

UploadPopup.propTypes = {
    title: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    uploadPath: PropTypes.string.isRequired,
    multiple: PropTypes.bool,
    accept: PropTypes.string,
    baseVariables: fileUploadVariablesType,
    extraVariablesComp: PropTypes.func,
    responseComp: PropTypes.func,
    onUpload: PropTypes.func,
    extraConfirmationMessage: PropTypes.string,
    children: PropTypes.node,
};

UploadPopup.defaultProps = {
    multiple: false,
    accept: null,
    baseVariables: [],
    extraVariablesComp: null,
    responseComp: null,
    onUpload: null,
    extraConfirmationMessage: null,
    children: null,
};

function UploadPopup(props) { // NOSONAR javascript:S3776
    const {
        title,
        onCancel,
        uploadPath,
        multiple,
        accept,
        baseVariables,
        extraVariablesComp,
        responseComp,
        onUpload,
        extraConfirmationMessage,
        children,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { maxUploadSizePerFile } = useSelector(getPrivateConfig);
    const fileUploads = useSelector(getFileUploads);
    const [selectedFileDescriptors, setSelectedFileDescriptors] = useState([]);
    const [variables, setVariables] = useState(baseVariables);
    const setExtraVariables = (newExtraVariables) => {
        const intersectionOfVariables = _.intersectionBy(baseVariables, newExtraVariables, 'name');
        if (intersectionOfVariables.length) {
            throw new Error(`Both 'baseVariables' and 'extraVariables' contain variables with the same names: ${intersectionOfVariables.map((variable) => variable.name).join(', ')}`);
        }
        setVariables([].concat(baseVariables, newExtraVariables));
    };
    const [isExtraVariablesValid, setExtraVariablesValid] = useState(false);
    const setSelectedFiles = (files) => {
        const newSelectedFileDescriptors = files.length ? Array.from(files).map((file) => ({
            file,
            isTooLarge: file.size > maxUploadSizePerFile,
            cancelTokenSource: axios.CancelToken.source(),
        })) : [];
        setSelectedFileDescriptors(newSelectedFileDescriptors);
        dispatch(initFileUploads(newSelectedFileDescriptors));
    };
    const onRemoveFile = (file) => () => {
        setSelectedFileDescriptors((prevSelectedFileDescriptors) => (
            prevSelectedFileDescriptors.filter((prevSelectedFileDescriptor) => prevSelectedFileDescriptor.file !== file)
        ));
        dispatch(removeFileUpload(file.name));
    };
    const onCancelTokenSourceChanged = (file) => (newCancelTokenSource) => {
        setSelectedFileDescriptors((prevSelectedFileDescriptors) => (
            prevSelectedFileDescriptors.map((prevSelectedFileDescriptor) => {
                const result = prevSelectedFileDescriptor;
                if (result.file === file) {
                    result.cancelTokenSource = newCancelTokenSource;
                }
                return result;
            })
        ));
    };
    const isUploadInProgress = fileUploads.some((fileUpload) => fileUpload.state === FileUploadState.IN_PROGRESS);
    const isUploadFilesButtonDisabled = isUploadInProgress
        || (extraVariablesComp && !isExtraVariablesValid)
        || !selectedFileDescriptors.length
        || selectedFileDescriptors.every((selectedFileDescriptor) => selectedFileDescriptor.isTooLarge);
    const [isUploadLeavePopupVisible, setUploadLeavePopupVisible] = useState(false);
    const onUploadLeaveCancel = () => setUploadLeavePopupVisible(false);
    const onUploadLeaveConfirmed = () => {
        setUploadLeavePopupVisible(false);
        selectedFileDescriptors.forEach(
            (selectedFileDescriptor) => (
                selectedFileDescriptor.cancelTokenSource.cancel(`Upload of file '${selectedFileDescriptor.file.name}' cancelled by user (leaving upload UI).`)
            ),
        );
        onCancel();
    };
    const onUploadLeave = () => {
        if (isUploadInProgress) {
            setUploadLeavePopupVisible(true);
        } else {
            onCancel();
        }
    };
    useEffectOnLoad(() => () => {
        dispatch(removeAllFileUploads());
    });
    return (
        <ModalDialog onCancel={onUploadLeave}>
            {isUploadLeavePopupVisible && (
                <ConfirmPopup
                    onConfirm={onUploadLeaveConfirmed}
                    onCancel={onUploadLeaveCancel}
                    message={t('file-upload.confirm-leave-upload')}
                />
            )}
            <div className="upload-popup-dialog" role="dialog" aria-modal>
                <div className="upload-popup-dialog--header">
                    <h4>{title}</h4>
                    <button type="button" className="close-button button-small button-primary-text upload-popup-dialog--close" onClick={onUploadLeave}>
                        <span aria-label={t('button.x')} title={t('button.x')} role="img">
                            <FontAwesomeIcon icon="times" />
                        </span>
                    </button>
                </div>
                {children && (
                    <div className="upload-popup-dialog--custom-content">
                        {children}
                    </div>
                )}
                <ChooseFileButton
                    onFilesSelection={setSelectedFiles}
                    multiple={multiple}
                    accept={accept}
                />
                {extraVariablesComp && (
                    <div className="upload-popup-dialog--extra-variables-component">
                        {isUploadInProgress && <div className="upload-popup-dialog--extra-variables-component--cover" />}
                        {extraVariablesComp(setExtraVariables, setExtraVariablesValid)}
                    </div>
                )}
                <div className="upload-popup-dialog--selected-files">
                    {
                        selectedFileDescriptors.length
                            ? selectedFileDescriptors.map((selectedFileDescriptor) => (
                                <UploadFileRow
                                    key={selectedFileDescriptor.file.name}
                                    fileDescriptor={selectedFileDescriptor}
                                    onRemoveFile={onRemoveFile(selectedFileDescriptor.file)}
                                    uploadPath={uploadPath}
                                    onCancelTokenSourceChanged={onCancelTokenSourceChanged(selectedFileDescriptor.file)}
                                    variables={variables}
                                    responseComp={responseComp}
                                    onUpload={onUpload}
                                />
                            ))
                            : (
                                <div className="empty upload-popup-dialog--selected-files--row">
                                    {t('file-upload.no-file-chosen')}
                                </div>
                            )
                    }
                </div>
                <div className="upload-popup-dialog--buttons">
                    <UploadFilesButton
                        fileDescriptors={selectedFileDescriptors.filter((selectedFileDescriptor) => !selectedFileDescriptor.isTooLarge)}
                        uploadPath={uploadPath}
                        disabled={isUploadFilesButtonDisabled}
                        variables={variables}
                        onUpload={onUpload}
                        extraConfirmationMessage={extraConfirmationMessage || t('misc.default-extra-confirmation-message')}
                        onCancelTokenSourceChanged={onCancelTokenSourceChanged}
                    />
                </div>
            </div>
        </ModalDialog>
    );
}

export default UploadPopup;
